import React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from './lib/utils';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    label?: string;
    error?: string;
    helperText?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
    ({ label, error, helperText, className = '', children, ...props }, ref) => {
        return (
            <div className="w-full">
                {label && (
                    <label
                        htmlFor={props.id}
                        className="block text-sm font-medium text-foreground mb-2"
                    >
                        {label}
                        {props.required && <span className="text-destructive ml-1">*</span>}
                    </label>
                )}
                <div className="relative">
                    <select
                        ref={ref}
                        {...props}
                        style={{
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            appearance: 'none',
                            backgroundImage: 'none',
                            ...props.style,
                        }}
                        className={cn(
                            'w-full px-4 py-2.5 pr-10',
                            'text-sm font-medium',
                            'bg-background',
                            'border rounded-lg',
                            'text-foreground',
                            '[appearance:none]',
                            '[&::-ms-expand]:hidden',
                            'cursor-pointer',
                            'transition-all duration-200',
                            'focus:outline-none focus:ring-2 focus:ring-offset-0',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                            error
                                ? 'border-destructive focus:ring-destructive focus:border-destructive'
                                : 'border-input focus:ring-ring focus:border-primary hover:border-muted-foreground',
                            className
                        )}
                    >
                        {children}
                    </select>
                    {/* Custom dropdown arrow */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    </div>
                </div>
                {error && (
                    <p className="mt-1.5 text-sm text-destructive">{error}</p>
                )}
                {helperText && !error && (
                    <p className="mt-1.5 text-sm text-muted-foreground">{helperText}</p>
                )}
            </div>
        );
    }
);

Select.displayName = 'Select';
